import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, CATEGORIES, CATEGORY_PRESETS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";
import { Sparkles, Crosshair, Loader2, TrendingUp, Mic, MicOff } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import LocationPicker from "@/components/LocationPicker";

export default function PostListing() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user } = useAuth();
  const { listing_id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const [loadingListing, setLoadingListing] = useState(isEditMode);

  // Categories that describe a PERSON (matrimonial, dating, job seeker) — require identity/
  // financial profile fields and never make sense as auctionable items, so we hide sale_type
  // entirely.
  const PROFILE_CATEGORIES = new Set(["matrimonial", "dating", "job"]);

  const [form, setForm] = useState({
    category: "service",
    subtype: "",
    title: "",
    description: "",
    price: 0,
    price_unit: "hour",
    city: "Mumbai",
    address: "",
    lat: 19.076,
    lng: 72.8777,
    tags: "",
    experience_years: 0,
    images: "",
    sale_type: "fixed",
    starting_bid: 0,
    min_increment: 0,
    buy_now_price: 0,
    ends_at: "",
    // Person-profile fields (matrimonial / dating / job)
    candidate_name: "",
    dob: "",
    contact_no: "",
    annual_income: "",
    family_income: "",
    family_profile: "",
    // Commodity real-time price fields
    commodity_type: "gold",
    margin_pct: 1.5,
    commodity_unit_label: "gram",
  });
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [livePrices, setLivePrices] = useState(null);
  // Voice-to-listing pre-fill (option 3a: safest — user reviews before publishing)
  const [voiceState, setVoiceState] = useState("idle"); // idle | listening | processing
  const voiceRecRef = useRef(null);

  const isProfileCategory = PROFILE_CATEGORIES.has(form.category);
  const isCommodity = form.category === "commodity";
  const isRealTime = isCommodity && form.sale_type === "real_time";

  // Live gold/silver feed — used to preview computed price when Real-time sale type is chosen.
  useEffect(() => {
    if (!isCommodity) return;
    let cancelled = false;
    api.get("/commodities/prices").then(r => {
      if (!cancelled) setLivePrices(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isCommodity]);

  // Voice intent router hand-off: if the global mic parsed a "post a…" intent it navigates
  // here with { state: { voice_draft, voice_transcript } }. Merge the draft on mount so the
  // user lands on a pre-filled form (option 3a — review before publish).
  useEffect(() => {
    if (isEditMode) return;
    const draft = routerLocation.state?.voice_draft;
    if (!draft) return;
    setForm(f => ({
      ...f,
      category: draft.category || f.category,
      title: draft.title || f.title,
      description: draft.description || f.description,
      price: draft.price || f.price,
      price_unit: draft.price_unit || f.price_unit,
      city: draft.city || f.city,
      address: draft.address || f.address,
      tags: (draft.tags && draft.tags.length) ? draft.tags.join(", ") : f.tags,
      experience_years: draft.experience_years || f.experience_years,
    }));
    toast.success("Pre-filled from your voice — review and tap Publish.");
    // Clear router state so a refresh doesn't re-apply the draft on top of user edits.
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode — fetch existing listing and prefill the form.
  useEffect(() => {
    if (!isEditMode || !editId) return;
    let cancelled = false;
    api.get(`/listings/${editId}`).then(r => {
      if (cancelled) return;
      const d = r.data;
      const ex = d.extra || {};
      // Detect real-time commodity: extras.commodity_type set → treat as real_time sale type.
      const isRT = d.category === "commodity" && !!ex.commodity_type;
      setForm(f => ({
        ...f,
        category: d.category || f.category,
        subtype: "",
        title: d.title || "",
        description: d.description || "",
        price: d.price || 0,
        price_unit: d.price_unit || "hour",
        city: d.city || "",
        address: d.address || "",
        lat: d.lat ?? 19.076,
        lng: d.lng ?? 72.8777,
        tags: (d.tags || []).join(", "),
        experience_years: d.experience_years || 0,
        images: (d.images || []).join("\n"),
        sale_type: isRT ? "real_time" : (d.sale_type || "fixed"),
        starting_bid: d.starting_bid || 0,
        min_increment: d.min_increment || 0,
        buy_now_price: d.buy_now_price || 0,
        ends_at: d.ends_at ? String(d.ends_at).slice(0, 16) : "",
        candidate_name: ex.candidate_name || "",
        dob: ex.dob || "",
        contact_no: ex.contact_no || "",
        annual_income: ex.annual_income_inr || "",
        family_income: ex.family_income_inr || "",
        family_profile: ex.family_profile || "",
        commodity_type: ex.commodity_type || "gold",
        margin_pct: ex.margin_pct ?? 1.5,
        commodity_unit_label: ex.unit_label || "gram",
      }));
      setLoadingListing(false);
    }).catch(() => {
      toast.error("Could not load the listing to edit.");
      navigate("/dashboard");
    });
    return () => { cancelled = true; };
  }, [isEditMode, editId, navigate]);

  // Uses browser GPS → reverse-geocode via backend (Mappls if creds set, OSM Nominatim otherwise)
  // and auto-fills city, address, lat/lng in one tap.
  const detectAndFill = () => {
    if (!navigator.geolocation) {
      toast.error("Location not available on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          const r = await api.get("/discover/reverse-geocode", { params: { lat, lng } });
          const d = r.data || {};
          setForm(f => ({
            ...f,
            lat: Number(lat.toFixed(6)),
            lng: Number(lng.toFixed(6)),
            city: d.city || f.city,
            address: d.formatted_address || d.area || f.address,
          }));
          if (d.city) {
            toast.success(`Auto-filled: ${d.city}${d.state ? ", " + d.state : ""}`);
          } else {
            toast.success("Location captured — please confirm city name.");
          }
        } catch {
          setForm(f => ({ ...f, lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }));
          toast.success("Coords captured. Please fill city manually.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast.error(err.code === 1 ? "Location permission blocked." : "Could not get location.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const preset = useMemo(() => CATEGORY_PRESETS[form.category] || {}, [form.category]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Login to post a listing</h2>
        <p className="text-stone-600 mb-6">Boost your work and reach nearby customers.</p>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="post-login-btn">Login with Google</Button>
      </div>
    );
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // When category changes, auto-fill category-specific defaults (subtype, unit, sale_type)
  // and reset subtype so the user must pick relevant option (e.g. Bride/Groom for matrimonial).
  const changeCategory = (cat) => {
    const p = CATEGORY_PRESETS[cat] || {};
    setForm((f) => ({
      ...f,
      category: cat,
      subtype: "",
      price_unit: p.price_unit || f.price_unit,
      sale_type: p.default_sale_type || "fixed",
    }));
  };

  // Voice-to-listing: user taps mic, describes the listing in their own words,
  // we send transcript to /voice-search/parse-listing → LLM extracts structured
  // fields → we merge into form state so the user can review + edit before publishing.
  const startVoiceFill = () => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      toast.error("Voice input isn't supported in this browser.");
      return;
    }
    if (voiceState !== "idle") return;
    const rec = new SR();
    rec.lang = navigator.language || "en-IN";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = async (evt) => {
      const transcript = evt.results[0][0].transcript;
      setVoiceState("processing");
      try {
        const { data } = await api.post("/voice-search/parse-listing", {
          transcript, lat: form.lat, lng: form.lng, lang: rec.lang,
        });
        const d = data.draft || {};
        // Merge draft into current form state. Preserve fields user has already typed if
        // the LLM returned blank (this is important for edit mode).
        setForm((f) => ({
          ...f,
          category: d.category || f.category,
          title: d.title || f.title,
          description: d.description || f.description,
          price: d.price || f.price,
          price_unit: d.price_unit || f.price_unit,
          city: d.city || f.city,
          address: d.address || f.address,
          tags: (d.tags && d.tags.length) ? d.tags.join(", ") : f.tags,
          experience_years: d.experience_years || f.experience_years,
        }));
        toast.success("Filled from your voice — review and tap Publish.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not understand — try again.");
      } finally {
        setVoiceState("idle");
      }
    };
    rec.onend = () => setVoiceState((s) => (s === "processing" ? s : "idle"));
    rec.onerror = () => { setVoiceState("idle"); toast.error("Mic error — please try again."); };
    setVoiceState("listening");
    try { rec.start(); } catch { setVoiceState("idle"); }
    voiceRecRef.current = rec;
    toast.info("Listening — describe your listing (e.g. 'I'm a plumber in Andheri, ₹500 per visit').");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (preset.subtype_required && !form.subtype) {
      toast.error(`Please select ${preset.subtype_label || "type"}`);
      return;
    }
    if (isProfileCategory) {
      // Enforce required profile fields for matrimonial / dating / job listings.
      const missing = [];
      if (!form.candidate_name.trim()) missing.push("Name");
      if (!form.dob) missing.push("Date of Birth");
      if (!form.contact_no.trim()) missing.push("Contact No");
      if (missing.length) {
        toast.error(`Please fill: ${missing.join(", ")}`);
        return;
      }
    }
    setSubmitting(true);
    // Dating photos take ~3-5s per image for AI KYC vision check — give the user a
    // clear status toast so they don't think the app hung.
    const isDating = form.category === "dating";
    const hasUploadedImage = form.images && form.images.split("\n").some(u => u.trim().startsWith("data:image"));
    const verifyToast = isDating && hasUploadedImage
      ? toast.loading("Verifying photo(s) with AI KYC… this takes 3-8s")
      : null;
    try {
      // Prepend the subtype into tags so backend & search remain unchanged.
      const explicitTags = form.tags.split(",").map((s) => s.trim()).filter(Boolean);
      const subtypeTag = form.subtype ? [form.subtype.toLowerCase()] : [];
      const tags = Array.from(new Set([...subtypeTag, ...explicitTags]));

      const extra = isProfileCategory
        ? {
            candidate_name: form.candidate_name.trim(),
            dob: form.dob,
            contact_no: form.contact_no.trim(),
            annual_income_inr: Number(form.annual_income) || 0,
            family_income_inr: Number(form.family_income) || 0,
            family_profile: form.family_profile.trim(),
          }
        : isRealTime
        ? {
            commodity_type: form.commodity_type,
            margin_pct: Number(form.margin_pct) || 0,
            unit_label: form.commodity_unit_label,
            unit_multiplier: ({ gram: 1, "10gram": 10, kg: 1000, tola: 11.664 })[form.commodity_unit_label] || 1,
          }
        : {};

      // Images can contain http URLs OR data URLs (which themselves contain commas).
      // We store them joined with newlines to avoid delimiter collision.
      const imagesArr = form.images
        ? form.images.split("\n").map(s => s.trim()).filter(Boolean)
        : [];

      const r = isEditMode
        ? await api.patch(`/listings/${editId}`, {
            category: form.category,
            title: form.title,
            description: form.description,
            price: Number(form.price) || 0,
            price_unit: form.price_unit,
            city: form.city,
            address: form.address,
            lat: Number(form.lat),
            lng: Number(form.lng),
            tags,
            experience_years: Number(form.experience_years) || 0,
            images: imagesArr,
            sale_type: isProfileCategory ? "fixed" : (isRealTime ? "fixed" : form.sale_type),
            extra,
          })
        : await api.post("/listings", {
            category: form.category,
            title: form.title,
            description: form.description,
            price: Number(form.price) || 0,
            price_unit: form.price_unit,
            city: form.city,
            address: form.address,
            lat: Number(form.lat),
            lng: Number(form.lng),
            tags,
            experience_years: Number(form.experience_years) || 0,
            images: imagesArr,
            // Person-profile categories skip auction fields; commodity real-time is stored as
            // "fixed" but the backend auto-recomputes price live via _apply_commodity_price.
            sale_type: isProfileCategory ? "fixed" : (isRealTime ? "fixed" : form.sale_type),
            extra,
          });
      if (verifyToast) toast.dismiss(verifyToast);
      toast.success(isEditMode ? "Listing updated!" : "Listing published!");
      navigate(`/listing/${r.data.listing_id}`);
    } catch (e) {
      if (verifyToast) toast.dismiss(verifyToast);
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{isEditMode ? "Edit listing" : "Post a listing"}</div>
      <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">
        {isEditMode ? "Update your listing" : "Get discovered by neighbours"}
      </h1>
      <p className="text-stone-600 mt-2">
        {isEditMode ? "Changes save the moment you hit publish." : "Takes just a minute. Add photos to stand out."}
      </p>

      {/* Voice-to-listing pre-fill — LLM extracts fields from spoken description */}
      {!isEditMode && (
        <div className="mt-5 rounded-2xl border border-[#DDA77B]/40 bg-gradient-to-r from-[#FFF3EC] to-white p-4 flex items-center gap-4"
             data-testid="voice-fill-banner">
          <button
            type="button"
            onClick={startVoiceFill}
            disabled={voiceState !== "idle"}
            className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center shadow-md transition-all ${
              voiceState === "listening"
                ? "bg-red-500 text-white animate-pulse"
                : voiceState === "processing"
                ? "bg-stone-400 text-white"
                : "bg-[#E05E36] text-white hover:bg-[#c94e2a]"
            }`}
            data-testid="voice-fill-btn"
            aria-label="Speak to fill listing"
          >
            {voiceState === "processing" ? (
              <Loader2 size={20} className="animate-spin" />
            ) : voiceState === "listening" ? (
              <MicOff size={20} />
            ) : (
              <Mic size={20} />
            )}
          </button>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest font-bold text-[#8B5E34] flex items-center gap-1.5">
              <Sparkles size={12} /> Speak to fill
            </div>
            <div className="text-sm text-stone-700 leading-snug">
              {voiceState === "listening"
                ? "Listening… describe your listing in one sentence."
                : voiceState === "processing"
                ? "Extracting fields from your speech…"
                : "Tap the mic and say something like \"I'm a plumber in Andheri, ₹500 per visit\". We'll pre-fill the form — you review and publish."}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-stone-200 rounded-3xl p-6 md:p-8">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Category</label>
          <Select value={form.category} onValueChange={changeCategory}>
            <SelectTrigger className="mt-2 rounded-full" data-testid="post-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {preset.subtypes && preset.subtypes.length > 0 && (
          <div className="bg-[#DDA77B]/10 border border-[#DDA77B]/30 rounded-2xl p-4">
            <label className="text-xs font-bold uppercase tracking-widest text-[#8B5E34] flex items-center gap-1.5">
              <Sparkles size={12} /> {preset.subtype_label || "Type"}
              {preset.subtype_required && <span className="text-[#E05E36]">*</span>}
            </label>
            <Select value={form.subtype} onValueChange={v => set("subtype", v)}>
              <SelectTrigger className="mt-2 rounded-xl bg-white" data-testid="post-subtype">
                <SelectValue placeholder={`Choose ${(preset.subtype_label || "type").toLowerCase()}…`} />
              </SelectTrigger>
              <SelectContent>
                {preset.subtypes.map(st => (
                  <SelectItem key={st} value={st} data-testid={`subtype-${st.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {st}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Person-profile fields — required for Matrimonial, Dating (18+), Job seeker categories.
            These represent an individual, not a product, so they replace commerce/sale controls. */}
        {isProfileCategory && (
          <div className="bg-[#2D5A27]/5 border border-[#2D5A27]/20 rounded-2xl p-5 space-y-4" data-testid="post-profile-fields">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A27] animate-pulse" />
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#2D5A27]">
                Person profile · {form.category === "matrimonial" ? "Matrimonial" : form.category === "dating" ? "Dating (18+)" : "Job Seeker"}
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                  Full Name <span className="text-[#E05E36]">*</span>
                </label>
                <Input
                  value={form.candidate_name}
                  onChange={e => set("candidate_name", e.target.value)}
                  placeholder="As on ID"
                  className="mt-2 rounded-xl"
                  required
                  data-testid="post-candidate-name"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                  Date of Birth <span className="text-[#E05E36]">*</span>
                </label>
                <Input
                  type="date"
                  value={form.dob}
                  onChange={e => set("dob", e.target.value)}
                  className="mt-2 rounded-xl"
                  required
                  data-testid="post-dob"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                  Contact No <span className="text-[#E05E36]">*</span>
                </label>
                <Input
                  type="tel"
                  value={form.contact_no}
                  onChange={e => set("contact_no", e.target.value)}
                  placeholder="+91 98765 43210"
                  className="mt-2 rounded-xl"
                  required
                  data-testid="post-contact-no"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                  Annual Income (₹)
                </label>
                <Input
                  type="number"
                  value={form.annual_income}
                  onChange={e => set("annual_income", e.target.value)}
                  placeholder="e.g. 800000"
                  className="mt-2 rounded-xl"
                  data-testid="post-annual-income"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                  Family Income (₹)
                </label>
                <Input
                  type="number"
                  value={form.family_income}
                  onChange={e => set("family_income", e.target.value)}
                  placeholder="Combined"
                  className="mt-2 rounded-xl"
                  data-testid="post-family-income"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
                Family Profile
              </label>
              <Textarea
                value={form.family_profile}
                onChange={e => set("family_profile", e.target.value)}
                placeholder="Father's occupation · Mother's occupation · Siblings · Home town · Values"
                className="mt-2 rounded-xl min-h-[80px]"
                data-testid="post-family-profile"
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Title</label>
          <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder={preset.title_placeholder || "e.g. Ramesh - AC Repair Expert"} className="mt-2 rounded-xl" required data-testid="post-title" />
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Description</label>
          <Textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder={preset.desc_placeholder || "What do you offer?"} className="mt-2 rounded-xl" required data-testid="post-description" />
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Price (₹)</label>
            <Input type="number" value={form.price} onChange={e => set("price", e.target.value)} className="mt-2 rounded-xl" data-testid="post-price" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Unit</label>
            <Select value={form.price_unit} onValueChange={v => set("price_unit", v)}>
              <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hour">per hour</SelectItem>
                <SelectItem value="per_visit">per visit</SelectItem>
                <SelectItem value="per_person">per person</SelectItem>
                <SelectItem value="per_piece">per piece</SelectItem>
                <SelectItem value="per_pair">per pair</SelectItem>
                <SelectItem value="day">per day</SelectItem>
                <SelectItem value="night">per night</SelectItem>
                <SelectItem value="month">per month</SelectItem>
                <SelectItem value="km">per km</SelectItem>
                <SelectItem value="gram">per gram</SelectItem>
                <SelectItem value="fixed">fixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Experience (yrs)</label>
            <Input type="number" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} className="mt-2 rounded-xl" data-testid="post-exp" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">City</label>
              <button
                type="button"
                onClick={detectAndFill}
                disabled={locating}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2D5A27] hover:text-[#E05E36] disabled:opacity-60"
                data-testid="post-detect-location"
              >
                {locating ? <Loader2 size={12} className="animate-spin" /> : <Crosshair size={12} />}
                {locating ? "Detecting…" : "Use my location"}
              </button>
            </div>
            <Input value={form.city} onChange={e => set("city", e.target.value)} className="mt-2 rounded-xl" data-testid="post-city" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Address / Landmark</label>
            <Input value={form.address} onChange={e => set("address", e.target.value)} className="mt-2 rounded-xl" data-testid="post-address" />
          </div>
        </div>

        {/* Phase 3 · dual-path location picker — 📍 use my current location OR
             🗺️ choose on map. GPS is never required. Any explicit map / search
             pick overrides the raw GPS value. */}
        <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3">
          <div className="text-[11px] font-black uppercase tracking-widest text-stone-500 mb-2">
            Pin exact location
          </div>
          <LocationPicker
            value={form.lat && form.lng
                     ? { lat: Number(form.lat), lng: Number(form.lng), city: form.city, source: "manual" }
                     : null}
            onChange={(loc) => setForm(f => ({
              ...f,
              lat:  Number(loc.lat.toFixed(6)),
              lng:  Number(loc.lng.toFixed(6)),
              city: loc.city || f.city,
            }))}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Latitude</label>
            <Input type="number" step="any" value={form.lat} onChange={e => set("lat", e.target.value)} className="mt-2 rounded-xl" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Longitude</label>
            <Input type="number" step="any" value={form.lng} onChange={e => set("lng", e.target.value)} className="mt-2 rounded-xl" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">
            {isProfileCategory ? "Profile Photo" : "Images"}
          </label>
          <div className="mt-2">
            <ImageUploader
              value={form.images}
              onChange={(v) => set("images", v)}
              maxImages={isProfileCategory ? 3 : 6}
              circular={isProfileCategory}
              helperText={
                isProfileCategory
                  ? "1-3 photos · Auto-resized · Your first photo is the profile hero"
                  : "JPG / PNG · Up to 6 · Auto-resized to 1200px width"
              }
            />
          </div>
          {isProfileCategory && (
            <div className="text-[11px] text-stone-500 mt-2 leading-snug">
              Profiles with a real photo get 5× more responses. For dating listings, the photo is
              also verified by our AI KYC vision model.
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Tags (comma-separated)</label>
          <Input value={form.tags} onChange={e => set("tags", e.target.value)} placeholder={preset.tags_placeholder || "plumber, leak, tap"} className="mt-2 rounded-xl" data-testid="post-tags" />
        </div>

        {!isProfileCategory && (
          <div className="bg-stone-50 rounded-2xl p-4 border border-stone-200">
            <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Sale type</label>
            <Select value={form.sale_type} onValueChange={v => set("sale_type", v)}>
              <SelectTrigger className="mt-2 rounded-xl" data-testid="post-saletype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed price / service</SelectItem>
                {isCommodity && (
                  <SelectItem value="real_time" data-testid="saletype-real-time">
                    Real-time market price (auto-updates)
                  </SelectItem>
                )}
                <SelectItem value="auction">Auction only</SelectItem>
                <SelectItem value="buy_or_bid">Auction + Buy Now</SelectItem>
              </SelectContent>
            </Select>

            {/* Real-time commodity pricing config */}
            {isRealTime && (
              <div className="mt-4 rounded-2xl border border-[#DDA77B]/40 bg-[#FFF3EC] p-4" data-testid="realtime-config">
                <div className="flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#E05E36]" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#8B5E34]">
                    Real-time price feed
                  </span>
                  <span className="ml-auto text-[10px] font-semibold text-stone-500">
                    Auto-refreshes with global market
                  </span>
                </div>

                <div className="mt-3 grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] font-bold text-stone-500">Metal / commodity</label>
                    <Select value={form.commodity_type} onValueChange={v => set("commodity_type", v)}>
                      <SelectTrigger className="mt-1 rounded-xl bg-white" data-testid="commodity-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gold">Gold (24K)</SelectItem>
                        <SelectItem value="silver">Silver</SelectItem>
                        <SelectItem value="platinum">Platinum</SelectItem>
                        <SelectItem value="copper">Copper</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-stone-500">Your margin (%)</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form.margin_pct}
                      onChange={e => set("margin_pct", e.target.value)}
                      className="mt-1 rounded-xl bg-white"
                      data-testid="commodity-margin"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-stone-500">Unit label</label>
                    <Select value={form.commodity_unit_label} onValueChange={v => set("commodity_unit_label", v)}>
                      <SelectTrigger className="mt-1 rounded-xl bg-white" data-testid="commodity-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gram">per gram</SelectItem>
                        <SelectItem value="10gram">per 10 gram</SelectItem>
                        <SelectItem value="kg">per kg</SelectItem>
                        <SelectItem value="tola">per tola</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {livePrices && (
                  <div className="mt-4 rounded-xl bg-white border border-stone-200 p-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
                      Market ({livePrices.source})
                    </span>
                    <span className="heading text-lg font-extrabold text-stone-900" data-testid="market-base-price">
                      ₹{(livePrices[form.commodity_type] || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-stone-400 text-xs">/g</span>
                    <span className="text-stone-400 text-xs">→ with +{Number(form.margin_pct || 0)}% margin =</span>
                    <span className="heading text-lg font-extrabold text-[#E05E36]" data-testid="listing-price-preview">
                      ₹{
                        (
                          (livePrices[form.commodity_type] || 0) *
                          (1 + Number(form.margin_pct || 0) / 100) *
                          ({ gram: 1, "10gram": 10, kg: 1000, tola: 11.664 })[form.commodity_unit_label]
                        ).toLocaleString("en-IN", { maximumFractionDigits: 2 })
                      }
                    </span>
                    <span className="text-stone-500 text-xs">
                      per {form.commodity_unit_label === "10gram" ? "10 g" : form.commodity_unit_label}
                    </span>
                  </div>
                )}
              </div>
            )}

            {(form.sale_type === "auction" || form.sale_type === "buy_or_bid") && (
              <div className="mt-3 grid md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[11px] font-bold text-stone-500">Starting bid (₹)</label>
                  <Input type="number" value={form.starting_bid} onChange={e => set("starting_bid", e.target.value)} className="mt-1 rounded-xl" data-testid="post-starting" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-stone-500">Min increment</label>
                  <Input type="number" value={form.min_increment} onChange={e => set("min_increment", e.target.value)} className="mt-1 rounded-xl" data-testid="post-increment" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-stone-500">Buy Now (₹)</label>
                  <Input type="number" value={form.buy_now_price} onChange={e => set("buy_now_price", e.target.value)} className="mt-1 rounded-xl" data-testid="post-buynow" />
                </div>
                <div>
                  <label className="text-[11px] font-bold text-stone-500">Ends at</label>
                  <Input type="datetime-local" value={form.ends_at} onChange={e => set("ends_at", e.target.value)} className="mt-1 rounded-xl" data-testid="post-endsat" />
                </div>
              </div>
            )}
          </div>
        )}

        <Button type="submit" disabled={submitting || loadingListing} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="submit-listing">
          {submitting ? (isEditMode ? "Updating…" : "Publishing…") : (isEditMode ? "Save Changes" : "Publish Listing")}
        </Button>
      </form>
    </div>
  );
}
